import Link from 'next/link';
import Image from 'next/legacy/image';
import styled from 'styled-components';
import { Button } from 'antd';

type Props = {
  title: string;
  subtitle: string;
  left: boolean;
  image: string;
  buttonText: string;
  href: string;
  isNextLink: boolean;
};

const Banner = styled.div<{ $left: boolean }>`
  position: relative;
  height: 40vh;
  min-height: 280px;
  background-image: ${(props) =>
    `url(/images/tangram_${props.$left ? 'left' : 'right'}.png)`};
  background-size: 100% 100%;
  background-position: center center;
  @media ${(props) => props.theme.breakpoints.lg} {
    min-height: 320px;
  }
  @media ${(props) => props.theme.breakpoints.md} {
    min-height: 280px;
  }
  @media ${(props) => props.theme.breakpoints.xs} {
    height: unset;
    min-height: 400px;
    background-image: unset;
  }
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${(props) => (props.$left ? 'flex-end' : 'flex-start')};
  padding: 14px ${(props) => props.theme.grid.getGridColumns(2, 1)};
`;

const ImageAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;

  .image {
    object-fit: contain;
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    .image {
      object-fit: cover;
    }
  }
`;

const Container = styled.div<{ $left: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${(props) => (props.$left ? 'flex-end' : 'flex-start')};
  text-align: ${(props) => (props.$left ? 'right' : 'left')};
  width: 100%;

  @media ${(props) => props.theme.breakpoints.xs} {
    border-radius: ${(props) => props.theme.borderRadius};
    background-color: #ffffff;
    opacity: 0.9;
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
    padding: 32px;
  }
`;

const SectionTitle = styled.h3`
  max-width: ${(props) => props.theme.grid.getGridColumns(14, 1)};
  @media ${(props) => props.theme.breakpoints.sm} {
    max-width: ${(props) => props.theme.grid.getGridColumns(13, 1)};
  }
  @media ${(props) => props.theme.breakpoints.xs} {
    max-width: inherit;
  }
  color: ${(props) => props.theme.colors.secondary};
  ${(props) => props.theme.font.h2}
`;

const SectionSubtitle = styled.p`
  ${(props) => props.theme.font.p1}
  max-width: ${(props) => props.theme.grid.getGridColumns(10, 1)};
  color: ${(props) => props.theme.colors.secondary};
  line-height: 125%;
  margin-bottom: 16px;
  @media ${(props) => props.theme.breakpoints.sm} {
    max-width: ${(props) => props.theme.grid.getGridColumns(11, 1)};
  }
  @media ${(props) => props.theme.breakpoints.xs} {
    max-width: inherit;
  }
`;

const Divider = styled.hr`
  width: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-top: 16px;
  margin-bottom: 16px;
  margin-left: var(--gutter);
  margin-right: 0;
  border: 1px solid ${(props) => props.theme.colors.primary};
  background-color: ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius};
`;

const BannerSection: React.FC<Props> = ({
  title,
  subtitle,
  left,
  image,
  buttonText,
  href,
  isNextLink,
}) => {
  const anchorTarget = isNextLink ? '_self' : '_blank';

  const button = (
    <Button type="primary" htmlType="button">
      <span
        key={buttonText.substring(0, 10) + new Date().getTime()}
        dangerouslySetInnerHTML={{
          __html: buttonText,
        }}
      />
    </Button>
  );

  return (
    <Banner as="div" $left={left}>
      <ImageAbsoluteContainer>
        <ImageContainer>
          <Image
            alt={image}
            className="image"
            layout="fill"
            objectPosition={`${left ? 'left' : 'right'} center`}
            src={`/images/${image}`}
            unoptimized
          />
        </ImageContainer>
      </ImageAbsoluteContainer>
      <Container as="div" $left={left}>
        <SectionTitle
          key={title.substring(0, 10) + new Date().getTime()}
          as="h3"
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
        <Divider />
        <SectionSubtitle
          key={subtitle.substring(0, 10) + new Date().getTime()}
          as="p"
          dangerouslySetInnerHTML={{
            __html: subtitle,
          }}
        />
        {isNextLink ? (
          <Link href={href} legacyBehavior>
            <a target={anchorTarget}>{button}</a>
          </Link>
        ) : (
          <a href={href}>{button}</a>
        )}
      </Container>
    </Banner>
  );
};

export default BannerSection;
